import {useMemo} from 'react'
import {MAIN_IMAGES_IMAGE_TYPE, MAIN_SET_PRODUCT_GALLERY_IMAGES_COUNT} from '../../../constants'
import {findImageGroupBy} from '../../../utils/image-groups-utils'
import {findMainSetProduct} from '../../../utils/product-utils'

const EMPTY_IMAGES = []

/**
 * Returns the main view images of the main set product of a product set.
 *
 * The main set product is resolved from `c_colorProduct` and its images already
 * reflect the colour currently selected in the PWA, because `useProductDetail`
 * merges the selected referenced product into `product.setProducts`.
 *
 * Only the images of the main view image group are returned - capped at
 * `MAIN_SET_PRODUCT_GALLERY_IMAGES_COUNT`. `c_extraMainImage` / `c_extraMainImage2`
 * are deliberately not included.
 *
 * The image group without variation attributes is used when none of the variation values of the
 * main set product is reflected in its main view image groups (for example a master product) and
 * when the variation specific lookup finds no match - the unified product holds the images of the
 * selected variant in that group, while picking an arbitrary variation specific group would show
 * the wrong colour.
 *
 * @param {Object} [product] - the product set
 * @returns {Array<Object>} main set product images, or an empty array when the
 *  product is not a set or has no main set product
 *
 * @example
 * const mainSetProductImages = useMainSetProductImages(product)
 */
const useMainSetProductImages = (product) => {
    const mainSetProduct = useMemo(() => findMainSetProduct(product), [product])
    const imageGroups = mainSetProduct?.imageGroups
    const variationValues = mainSetProduct?.variationValues

    return useMemo(() => {
        if (!imageGroups) {
            return EMPTY_IMAGES
        }

        const mainViewImageGroups = imageGroups.filter(
            ({viewType}) => viewType === MAIN_IMAGES_IMAGE_TYPE
        )

        // a variation value is only usable when the image groups are refined by it
        const hasSelectedVariation = mainViewImageGroups.some(({variationAttributes = []}) =>
            variationAttributes.some(({id}) => variationValues?.[id] !== undefined)
        )

        const unrefinedImageGroup = mainViewImageGroups.find(
            ({variationAttributes}) => !variationAttributes?.length
        )

        const imageGroup =
            (hasSelectedVariation &&
                findImageGroupBy(mainViewImageGroups, {
                    viewType: MAIN_IMAGES_IMAGE_TYPE,
                    selectedVariationAttributes: variationValues
                })) ||
            unrefinedImageGroup

        const images = imageGroup?.images

        return images?.length
            ? images.slice(0, MAIN_SET_PRODUCT_GALLERY_IMAGES_COUNT)
            : EMPTY_IMAGES
    }, [imageGroups, variationValues])
}

export default useMainSetProductImages
